/**
 * One landmark per stay city, the place guests measure a hotel against.
 *
 * Coordinates from Wikipedia (Jemaa el-Fnaa, Bab Bou Jeloud, Hassan Tower,
 * Hassan II Mosque) and OpenStreetMap (Grand Socco, Agadir Marina), checked
 * 2026-09-28. A city missing here simply shows no line on its cards.
 */
export interface Landmark {
  /** Key under stays.landmarks.* in the locale files. */
  key: string;
  lat: number;
  lng: number;
}

export const STAY_LANDMARKS: Record<string, Landmark> = {
  marrakech: { key: "jemaa_el_fnaa", lat: 31.62583, lng: -7.98944 },
  fez: { key: "bab_boujloud", lat: 34.06167, lng: -4.98389 },
  rabat: { key: "hassan_tower", lat: 34.02416, lng: -6.82283 },
  casablanca: { key: "hassan_ii_mosque", lat: 33.6085, lng: -7.6327 },
  tangier: { key: "grand_socco", lat: 35.7843, lng: -5.81309 },
  agadir: { key: "agadir_marina", lat: 30.42426, lng: -9.61736 },
};

/** Straight-line distance in km. */
function haversineKm(aLat: number, aLng: number, bLat: number, bLng: number): number {
  const rad = (d: number) => (d * Math.PI) / 180;
  const dLat = rad(bLat - aLat);
  const dLng = rad(bLng - aLng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(aLat)) * Math.cos(rad(bLat)) * Math.sin(dLng / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(h));
}

/*
 * Streets are not straight lines, least of all in a medina, so the crow-flies
 * distance is stretched by a quarter before it becomes minutes. Walking at
 * 5 km/h; driving at 25 km/h, a city-traffic pace. Past a 25-minute walk
 * nobody walks, so the line switches to the car. The copy says "about".
 */
const DETOUR = 1.25;
const WALK_KMH = 5;
const DRIVE_KMH = 25;
const MAX_WALK_MIN = 25;
/** Beyond this the landmark is not what the stay is near; say nothing. */
const MAX_DRIVE_MIN = 45;

export interface NearbyLandmark {
  key: string;
  mode: "walk" | "drive";
  minutes: number;
}

export function nearbyLandmark(
  citySlug: string,
  lat: number | null | undefined,
  lng: number | null | undefined,
): NearbyLandmark | null {
  const l = STAY_LANDMARKS[citySlug];
  if (!l || typeof lat !== "number" || typeof lng !== "number") return null;
  const km = haversineKm(lat, lng, l.lat, l.lng) * DETOUR;
  const walk = Math.max(1, Math.round((km / WALK_KMH) * 60));
  if (walk <= MAX_WALK_MIN) return { key: l.key, mode: "walk", minutes: walk };
  // Rounded to 5: "about 17 min by car" claims a precision nobody has.
  const drive = Math.max(5, Math.round((km / DRIVE_KMH) * 12) * 5);
  return drive <= MAX_DRIVE_MIN ? { key: l.key, mode: "drive", minutes: drive } : null;
}
